import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AppLayout from "../components/AppLayout";
import { api, errMsg, formatCurrency, formatDate, computePlan, variance, UNIT_HOURS, UNIT_HOURS_EXTENDED, taskHoursPerUnit, diffBaseline } from "../lib/api";
import { Save, Plus, Trash2, Download, FileText, GitBranch, Clock, AlertTriangle, CheckCircle2, TrendingUp, CalendarDays, DollarSign, CreditCard, History, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import Gantt from "../components/Gantt";
import AuditDrawer from "../components/AuditDrawer";
import RiskScore from "../components/RiskScore";
import RiskRegister from "../components/RiskRegister";
import WeeklyReportsPanel from "../components/WeeklyReportsPanel";
import ChangesPanel from "../components/ChangesPanel";
import CostEstimateButton from "../components/CostEstimateButton";
import CostEstimateHistory from "../components/CostEstimateHistory";
import ExecutiveSummaryButton from "../components/ExecutiveSummaryButton";
import ShareLinkButton from "../components/ShareLinkButton";
import PdfExportButton from "../components/PdfExportButton";
import TaskCsvImportButton from "../components/TaskCsvImportButton";
import TrialBanner, { useSubscription } from "../components/TrialBanner";
import { useProjectWorkspace } from "../hooks/useProjectWorkspace";
import { exportProjectPDF } from "../lib/pdf";
import StakeholderPicker, { stakeholderNames } from "../components/StakeholderPicker";
import TaskColumnsMenu, { EXTRA_TASK_COLUMNS } from "../components/TaskColumnsMenu";
import TaskCustomFieldsButton from "../components/TaskCustomFieldsButton";
import PlanSprintsButton from "../components/PlanSprintsButton";
import SprintsPanel from "../components/SprintsPanel";

const STATUS = ["Not Started", "In Progress", "Completed", "Blocked"];
const ORDER_TYPES = ["Start With", "Start After", "Start Before"]; // "Start Before" = lead (negative lag allowed)
const LEAD_ORDER_TYPES = new Set(["Start Before"]);
const SHOW_DEPS_KEY = "compass_pm.showDeps.v1";
const UNITS = ["Minutes", "Hours", "Days", "Weeks"];

export default function ProjectWorkspace() {
  const { id } = useParams();
  const { locked } = useSubscription();
  const nav = useNavigate();
  const {
    project, setProject,
    tasks, setTasks,
    roles, teammates, programs, stakeholders,
    risk, refreshRisk,
    loading, load,
  } = useProjectWorkspace(id);
  const { user, setUser } = useAuth();
  const hiddenCols = user?.hidden_task_columns || [];
  const extraCols = EXTRA_TASK_COLUMNS.filter((c) => !hiddenCols.includes(c.k));
  const setHiddenCols = async (next) => {
    setUser((u) => ({ ...u, hidden_task_columns: next }));
    try { await api.put("/account/preferences", { hidden_task_columns: next }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const [view, setView] = useState("plan"); // plan | actuals | performance | risks
  const [showDeps, setShowDeps] = useState(() => {
    try {
      const stored = window.localStorage.getItem(SHOW_DEPS_KEY);
      return stored === null ? true : stored === "1";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem(SHOW_DEPS_KEY, showDeps ? "1" : "0"); }
    catch (e) { console.warn("Failed to persist showDeps preference:", e); }
  }, [showDeps]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [costArchiveKey, setCostArchiveKey] = useState(0);
  const [focusedRowIdx, setFocusedRowIdx] = useState(-1);

  const timeUnit = project?.time_unit || "Days";
  const plan = useMemo(()=>computePlan(tasks, roles, timeUnit), [tasks, roles, timeUnit]);
  const baselineDiff = useMemo(()=>diffBaseline(tasks, project?.baseline, roles, timeUnit), [tasks, project?.baseline, roles, timeUnit]);
  const hasBaseline = !!project?.baseline && project.baseline.length > 0;

  const scheduleVar = variance(project?.stated_due_date, plan.plannedDelivery);
  const budgetVar = (plan.estimatedCost || 0) - Number(project?.stated_budget || 0);
  const actualBudgetVar = plan.actualCost - plan.estimatedCost;
  const actualScheduleVar = variance(plan.plannedDelivery, plan.actualDelivery);

  // Keyboard shortcuts for the task table: J/K to navigate rows, E to edit focused row's name.
  // Ignored when the user is already typing in an input / select / textarea.
  useEffect(() => {
    const inField = (el) => el && (el.tagName === "INPUT" || el.tagName === "SELECT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inField(e.target)) return;
      if (view === "risks") return;
      const n = tasks.length;
      if (n === 0) return;
      if (e.key === "j" || e.key === "J" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedRowIdx((i) => Math.min(n - 1, (i < 0 ? 0 : i + 1)));
      } else if (e.key === "k" || e.key === "K" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedRowIdx((i) => Math.max(0, (i < 0 ? 0 : i - 1)));
      } else if (e.key === "e" || e.key === "E") {
        e.preventDefault();
        setFocusedRowIdx((i) => {
          const idx = i < 0 ? 0 : i;
          // Defer the focus until after the state tick / render settles
          setTimeout(() => {
            const row = document.querySelector(`tr[data-row-idx="${idx}"]`);
            const nameInput = row?.querySelector('input[data-task-field="name"]');
            if (nameInput) {
              nameInput.focus();
              nameInput.select?.();
              nameInput.scrollIntoView({ block: "nearest" });
            }
          }, 0);
          return idx;
        });
      } else if (e.key === "Escape") {
        setFocusedRowIdx(-1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tasks.length, view]);

  // Scroll the focused row into view when it changes via keyboard
  useEffect(() => {
    if (focusedRowIdx < 0) return;
    const row = document.querySelector(`tr[data-row-idx="${focusedRowIdx}"]`);
    row?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusedRowIdx]);

  const saveProject = async (patch) => {
    try {
      const next = { ...project, ...patch };
      setProject(next);
      await api.put(`/projects/${id}`, { data: { name: next.name, description: next.description, stakeholders: next.stakeholders, stakeholder_ids: next.stakeholder_ids || [], pm_teammate_id: next.pm_teammate_id || null, project_owner_teammate_id: next.project_owner_teammate_id || null, program_id: next.program_id, stated_due_date: next.stated_due_date, stated_budget: Number(next.stated_budget||0), time_unit: next.time_unit, other_details: next.other_details } });
    } catch(e){ toast.error(errMsg(e)); }
  };

  const addTask = async () => {
    try {
      const nextNum = (tasks.reduce((m,t)=>Math.max(m, Number(t.task_number||0)), 0)) + 1;
      const { data } = await api.post(`/projects/${id}/tasks`, { data: { task_number: nextNum, name: "New task", estimated_time: 1, order_type: "Start After", status: "Not Started" } });
      setTasks((x)=>[...x, data]);
    } catch(e){ toast.error(errMsg(e)); }
  };

  const updateTask = async (taskId, patch) => {
    setTasks((xs)=>xs.map((t)=>t.id===taskId ? {...t, ...patch}:t));
    try {
      const { data } = await api.put(`/projects/${id}/tasks/${taskId}`, { data: patch });
      if (patch.status && data?.actual_completion_date) setTasks((xs)=>xs.map((t)=>t.id===taskId ? {...t, actual_completion_date: data.actual_completion_date}:t));
    }
    catch(e){ toast.error(errMsg(e)); load(); }
  };

  const removeTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/projects/${id}/tasks/${taskId}`); setTasks((xs)=>xs.filter(t=>t.id!==taskId)); } catch(e){ toast.error(errMsg(e)); }
  };

  const saveBaseline = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/baseline`);
      toast.success(`Baseline saved (${data.task_count} tasks)`);
      load();
    } catch(e){ toast.error(errMsg(e)); }
  };

  const saveAsTemplate = async () => {
    try { await api.post(`/projects/${id}/save-as-template`); toast.success("Saved as template"); } catch(e){ toast.error(errMsg(e)); }
  };

  const customFields = project?.task_custom_fields || [];
  const hasSprints = !!project?.sprint_config;
  const sprintNumbers = useMemo(() => [...new Set(tasks.filter((t) => t.is_sprint && t.sprint_number).map((t) => t.sprint_number))].sort((a, b) => a - b), [tasks]);
  const lastSprint = sprintNumbers[sprintNumbers.length - 1];
  const sprintRoleIds = tasks.filter((t) => t.is_sprint && t.sprint_number === lastSprint && t.role_id).sort((a, b) => (a.sprint_role_index || 0) - (b.sprint_role_index || 0)).map((t) => t.role_id);
  const [sprintKey, setSprintKey] = useState(0);
  const onSprintsPlanned = (res) => { setProject((p) => ({ ...p, sprint_config: res.sprint_config })); setSprintKey((k) => k + 1); load(); };
  const saveCustomFields = async (next) => {
    setProject((p) => ({ ...p, task_custom_fields: next }));
    try { await api.put(`/projects/${id}`, { data: { task_custom_fields: next } }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const updateCustom = (t, key, v) => updateTask(t.id, { custom_fields: { ...(t.custom_fields || {}), [key]: v } });

  const exportCSV = () => {
    const headers = ["#","Name","Role","Resource","Order","OrderType",`Lag (${timeUnit})`,`EstTime (${timeUnit})`,"EstCost",`ActualTime (${timeUnit})`,"ActualCost","ActualStart","ActualEnd","Status","Ext","Notes","PO #","Invoice #","Vendor","Vendor Contact","Fulfillment Request #", ...(hasSprints ? ["Sprint","Story Points"] : []), ...customFields.map((f)=>f.label)];
    const rows = tasks.map(t=>{
      const estCost = roleRate(t)*taskHoursPerUnit(t,timeUnit)*Number(t.estimated_time||0);
      const autoActual = roleRate(t)*taskHoursPerUnit(t,timeUnit)*Number(t.actual_time||0);
      const effectiveActual = t.actual_cost != null ? Number(t.actual_cost) : autoActual;
      return [t.task_number,t.name,t.role_name||"",t.resource_name||"",t.order||"",t.order_type||"",t.lag||0,t.estimated_time||0, estCost,t.actual_time||"",effectiveActual || "",t.actual_start_date||"",t.actual_completion_date||"",t.status||"",t.extended_hours?"Y":"",t.notes||"",t.po_number||"",t.invoice_number||"",t.vendor||"",t.vendor_contact||"",t.fulfillment_request_number||"", ...(hasSprints ? [t.is_sprint ? `Sprint block ${t.sprint_number}` : (t.sprint_number ? `Sprint ${t.sprint_number}` : "Backlog"), t.story_points ?? ""] : []), ...customFields.map((f)=>(t.custom_fields||{})[f.key]||"")];
    });
    const csv = [headers, ...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${project?.name || "project"}.csv`; a.click();
  };
  const exportPDF = async () => {
    try {
      const programName = programs.find((p) => p.id === project?.program_id)?.name;
      toast.message("Building status pack…");
      let risks = [];
      try {
        const { data } = await api.get(`/projects/${id}/risks`);
        risks = data || [];
      } catch (e) { console.warn("Could not load risks for PDF — proceeding without", e); }
      await exportProjectPDF({ project, plan, tasks, roles, risk, programName, risks });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/share`);
      const url = `${window.location.origin}/share/${data.share_token}`;
      // Commit state first so the Share-link modal can display the URL even if
      // the browser blocks clipboard access (headless / insecure context / Safari).
      setProject((p) => ({ ...p, share_token: data.share_token, share_enabled: true }));
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.success("Share link ready — tap Copy inside the dialog");
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revokeShare = async () => {
    try {
      await api.delete(`/projects/${id}/share`);
      setProject((p) => ({ ...p, share_enabled: false }));
      toast.success("Share link revoked");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleShareRisks = async (next) => {
    setProject((p) => ({ ...p, share_show_risks: next }));
    try {
      await api.put(`/projects/${id}/share/settings`, { data: { share_show_risks: next } });
      toast.success(next ? "Top 3 risks will show on the client portal" : "Risks hidden from client portal");
    } catch (e) {
      toast.error(errMsg(e));
      setProject((p) => ({ ...p, share_show_risks: !next }));
    }
  };

  // Pre-compute _estDays alias on tasks for Gantt (keeps bar labels tidy)
  plan.tasks.forEach((t) => (t._estDays = t._estDays || 0));

  function unitHours(u){ return UNIT_HOURS[u] || 8; }
  function roleRate(t){ const r = roles.find(r=>r.id===t.role_id); return r?Number(r.rate||0):0; }

  if (loading || !project) return <AppLayout><div className="text-slate-400">Loading project…</div></AppLayout>;

  const filteredTeammates = (roleId) => roleId ? teammates.filter((tm)=>tm.role_id === roleId) : teammates;

  const requireActive = (fn) => (...args) => {
    if (locked) { toast.error("Upgrade to Compass PM Pro to use this feature"); nav("/pricing"); return; }
    return fn(...args);
  };

  const summaryCards = [
    { id:"calc-delivery", label:"Calculated Delivery", value: plan.plannedDelivery ? formatDate(plan.plannedDelivery) : "—", icon: CalendarDays, color:"text-blue-300" },
    { id:"est-cost", label:"Estimated Cost", value: formatCurrency(plan.estimatedCost), icon: DollarSign, color:"text-blue-300" },
    { id:"actual-delivery", label:"Actual Delivery", value: plan.actualDelivery ? formatDate(plan.actualDelivery) : "—", icon: Clock, color:"text-slate-200" },
    { id:"actual-cost", label:"Actual Cost", value: formatCurrency(plan.actualCost), icon: CreditCard, color:"text-slate-200" },
    { id:"schedule-variance", label:"Schedule Variance", value: scheduleVar===null?"—":(scheduleVar>0?`+${scheduleVar}d late`:`${Math.abs(scheduleVar)}d ahead`), icon: TrendingUp, color: scheduleVar>0?"text-rose-300":"text-emerald-300" },
    { id:"budget-variance", label:"Budget Variance", value: formatCurrency(budgetVar), icon: AlertTriangle, color: budgetVar>0?"text-rose-300":"text-emerald-300" },
  ];

  return (
    <AppLayout>
      <TrialBanner />
      {/* Project header */}
      <div className="pc-card p-6 mb-6" data-testid="project-details">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex-1 min-w-[260px]">
            <input value={project.name||""} onChange={(e)=>saveProject({name:e.target.value})} data-testid="project-name-input"
              className="text-2xl sm:text-3xl font-bold text-white bg-transparent border-0 w-full focus:outline-none px-0" style={{fontFamily:'Outfit'}} />
            <textarea value={project.description||""} onChange={(e)=>saveProject({description:e.target.value})} data-testid="project-description-input"
              placeholder="Project description" rows={2} className="w-full mt-1 bg-transparent border-0 text-slate-300 focus:outline-none p-0 resize-none" />
          </div>
          <div className="flex flex-wrap gap-2">
            <ExecutiveSummaryButton projectId={id} locked={locked} onLocked={()=>{ toast.error("Upgrade to Compass PM Pro to use AI summaries"); nav("/pricing"); }} />
            <button onClick={requireActive(saveBaseline)} data-testid="save-baseline-btn" disabled={locked} className={`inline-flex items-center gap-2 border border-slate-700 ${locked?"opacity-50":"hover:border-blue-500"} text-slate-200 px-3 py-2 rounded-lg text-sm`}><Save size={14}/> Save baseline</button>
            <button onClick={requireActive(saveAsTemplate)} data-testid="save-template-btn" disabled={locked} className={`inline-flex items-center gap-2 border border-slate-700 ${locked?"opacity-50":"hover:border-blue-500"} text-slate-200 px-3 py-2 rounded-lg text-sm`}><FileText size={14}/> Save as template</button>
            <button onClick={() => setAuditOpen(true)} data-testid="open-audit-btn" className="inline-flex items-center gap-2 border border-slate-700 hover:border-blue-500 text-slate-200 px-3 py-2 rounded-lg text-sm"><History size={14}/> Audit log</button>
            <ShareLinkButton
              project={project}
              locked={locked}
              onShare={share}
              onRevoke={revokeShare}
              onToggleRisks={toggleShareRisks}
              requireActive={requireActive}
            />
            <button onClick={requireActive(exportCSV)} data-testid="export-csv-btn" disabled={locked} className={`inline-flex items-center gap-2 border border-slate-700 ${locked?"opacity-50":"hover:border-blue-500"} text-slate-200 px-3 py-2 rounded-lg text-sm`}><Download size={14}/> CSV</button>
            <PdfExportButton onExport={exportPDF} locked={locked} requireActive={requireActive} />
            <CostEstimateButton
              projectId={id}
              project={project}
              tasks={tasks}
              roles={roles}
              timeUnit={timeUnit}
              authorName={user?.name || user?.email || ""}
              savePathHint={project.report_save_path || ""}
              onArchived={() => setCostArchiveKey((k) => k + 1)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2 lg:col-span-4" data-testid="field-stakeholders">
            <label className="text-[10px] uppercase tracking-widest text-slate-400">Stakeholders</label>
            <div className="mt-1">
              <StakeholderPicker stakeholders={stakeholders} selected={project.stakeholder_ids || []}
                onChange={(ids)=>saveProject({ stakeholder_ids: ids, stakeholders: stakeholderNames(stakeholders, ids) })} testId="project-stakeholder-picker" />
              {!(project.stakeholder_ids||[]).length && project.stakeholders && (
                <div className="text-xs text-slate-500 mt-1" data-testid="legacy-stakeholders">Previously listed: {project.stakeholders}</div>
              )}
            </div>
          </div>
          <SelectField label="Project owner" value={project.project_owner_teammate_id||""} options={[{v:"",l:"Unassigned"},...teammates.map(tm=>({v:tm.id,l:tm.name}))]} onChange={(v)=>saveProject({project_owner_teammate_id:v||null})} testid="field-project-owner"/>
          <SelectField label="Project manager" value={project.pm_teammate_id||""} options={[{v:"",l:"Unassigned"},...teammates.map(tm=>({v:tm.id,l:tm.name}))]} onChange={(v)=>saveProject({pm_teammate_id:v||null})} testid="field-pm"/>
          <SelectField label="Program" value={project.program_id||""} options={[{v:"",l:"— None —"},...programs.map(p=>({v:p.id,l:p.name}))]} onChange={(v)=>saveProject({program_id:v||null})} testid="field-program"/>
          <Field label="Due date" type="date" value={project.stated_due_date||""} onChange={(v)=>saveProject({stated_due_date:v})} testid="field-due-date"/>
          <Field label="Budget" type="number" value={project.stated_budget||0} onChange={(v)=>saveProject({stated_budget:Number(v)})} testid="field-budget"/>
          <SelectField label="Time unit" value={project.time_unit||"Days"} options={UNITS.map(u=>({v:u,l:u}))} onChange={(v)=>saveProject({time_unit:v})} testid="field-time-unit"/>
          <Field label="Other details" value={project.other_details||""} onChange={(v)=>saveProject({other_details:v})} testid="field-other" className="sm:col-span-2 lg:col-span-3"/>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-6" data-testid="summary-cards">
        {summaryCards.map((c)=> (
          <div key={c.id} className="pc-card p-4" data-testid={`summary-${c.id}`}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-blue-300/80">
              <c.icon size={12}/> {c.label}
            </div>
            <div className={`text-xl font-bold mt-1.5 ${c.color}`} style={{fontFamily:'Outfit'}}>{c.value}</div>
          </div>
        ))}
      </div>

      {risk && (
        <div className="mb-6">
          <RiskScore risk={risk}/>
        </div>
      )}

      <WeeklyReportsPanel projectId={id} project={project} stakeholders={stakeholders} onUpdate={(patch) => setProject((p) => ({ ...p, ...patch }))}/>
      <CostEstimateHistory projectId={id} refreshKey={costArchiveKey} savePathHint={project.report_save_path || ""} />

      {/* View toggles */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="inline-flex max-w-full rounded-lg border border-slate-700 overflow-x-auto" data-testid="view-toggle">
          {[{k:"plan",l:"Plan"},{k:"actuals",l:"Actuals"},{k:"performance",l:"Performance"},{k:"risks",l:"Risks"},{k:"sprints",l:"Sprints"},{k:"changes",l:"Changes"}].map((o)=> (
            <button key={o.k} onClick={()=>setView(o.k)} data-testid={`view-toggle-${o.k}`}
              className={`px-4 py-2 text-sm ${view===o.k?"bg-blue-600 text-white":"text-slate-300 hover:bg-slate-800"}`}>{o.l}</button>
          ))}
        </div>
        <button data-testid="toggle-dependencies-btn" onClick={()=>setShowDeps(!showDeps)}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${showDeps?"border-blue-500 text-blue-300 bg-blue-500/10":"border-slate-700 text-slate-300"}`}><GitBranch size={14}/> {showDeps?"Hide":"Show"} dependencies</button>
        <span className="hidden md:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500" data-testid="kbd-hint" title="Keyboard shortcuts for the task table">
          <kbd className="px-1.5 py-0.5 border border-slate-700 rounded font-mono text-slate-400">J</kbd>
          <kbd className="px-1.5 py-0.5 border border-slate-700 rounded font-mono text-slate-400">K</kbd>
          <span className="text-slate-500">rows</span>
          <kbd className="ml-1 px-1.5 py-0.5 border border-slate-700 rounded font-mono text-slate-400">E</kbd>
          <span className="text-slate-500">edit</span>
        </span>
        {plan.criticalPath?.length > 0 && (
          <div className="inline-flex items-center gap-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 px-3 py-1.5 rounded-lg" data-testid="critical-path-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse"></span>
            Critical path: {plan.criticalPath.join(" → ")}
          </div>
        )}
        {hasBaseline && (
          <span className="inline-flex items-center gap-2 text-xs text-blue-300 bg-blue-500/10 border border-blue-500/30 px-3 py-1.5 rounded-lg" data-testid="baseline-badge">
            Baseline saved {project.baseline_saved_at ? formatDate(project.baseline_saved_at) : ""}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2 flex-wrap justify-end">
        {view !== "risks" && view !== "changes" && (
          <PlanSprintsButton projectId={id} project={project} roles={roles} sprintCount={lastSprint || 0} sprintRoleIds={sprintRoleIds} onPlanned={onSprintsPlanned} />
        )}
        {view !== "risks" && view !== "changes" && view !== "sprints" && (
          <>
            <TaskColumnsMenu hidden={hiddenCols} onChange={setHiddenCols} />
            <TaskCustomFieldsButton fields={customFields} onChange={saveCustomFields} />
            <TaskCsvImportButton projectId={id} onImported={load} locked={locked} onLocked={() => { toast.error("Upgrade to Compass PM Pro to use this feature"); nav("/pricing"); }} />
            <button onClick={addTask} data-testid="add-task-btn" className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg shadow-blue-900/30"><Plus size={14}/> Add task</button>
          </>
        )}
        </div>
      </div>

      {view === "risks" && (
        <RiskRegister projectId={id} tasks={tasks} locked={locked} onChanged={refreshRisk} />
      )}

      {view === "changes" && (
        <ChangesPanel projectId={id} />
      )}

      {view === "sprints" && (
        <SprintsPanel projectId={id} refreshKey={`${sprintKey}-${tasks.map((t)=>`${t.sprint_number||""}:${t.story_points||""}:${t.status}:${t.actual_completion_date||""}`).join("|")}`} />
      )}

      {/* Tasks */}
      {view !== "risks" && view !== "changes" && view !== "sprints" && (
      <div className="pc-card overflow-x-auto" data-testid="tasks-table">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-blue-300/80 border-b border-slate-800">
            <tr>
              <th className="p-3 w-10">#</th>
              <th className="p-3">Task</th>
              <th className="p-3">Role</th>
              <th className="p-3">Resource</th>
              {showDeps && <>
                <th className="p-3">Order</th>
                <th className="p-3">Order Type</th>
                <th className="p-3">Lag ({timeUnit})</th>
              </>}
              {view!=="actuals" && <>
                <th className="p-3">Est {timeUnit}</th>
                <th className="p-3" title="24h/day instead of 8h/day for this task"><span className="whitespace-nowrap">Ext</span></th>
                <th className="p-3">Est Cost</th>
              </>}
              {view!=="plan" && <>
                <th className="p-3">Actual {timeUnit}</th>
                <th className="p-3">Actual Cost</th>
                <th className="p-3">Actual Start</th>
                <th className="p-3">Actual End</th>
              </>}
              {hasSprints && <>
                <th className="p-3" data-testid="task-col-header-sprint">Sprint</th>
                <th className="p-3 whitespace-nowrap" data-testid="task-col-header-points">Points</th>
              </>}
              {extraCols.map((c)=> <th key={c.k} className="p-3 whitespace-nowrap" data-testid={`task-col-header-${c.k}`}>{c.l}</th>)}
              {customFields.map((f)=> <th key={f.key} className="p-3 whitespace-nowrap text-blue-200" data-testid={`task-custom-col-header-${f.key}`}>{f.label}</th>)}
              <th className="p-3">Status</th>
              {view==="performance" && <th className="p-3">Variance</th>}
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {tasks.length === 0 && (
              <tr><td colSpan={24} className="p-10 text-center text-slate-400" data-testid="tasks-empty">No tasks yet — click "Add task" to begin.</td></tr>
            )}
            {tasks.map((t, idx)=> {
              const planTask = plan.tasks.find(p=>p.id===t.id) || t;
              const isCritical = !!planTask._critical;
              const estCost = roleRate(t) * taskHoursPerUnit(t, timeUnit) * Number(t.estimated_time||0);
              // Auto-calculated actual cost from hours; manual entry in actual_cost overrides.
              const autoActualCost = roleRate(t) * taskHoursPerUnit(t, timeUnit) * Number(t.actual_time||0);
              const effectiveActualCost = t.actual_cost != null ? Number(t.actual_cost) : autoActualCost;
              const taskVar = effectiveActualCost - estCost;
              const flagLate = Number(t.actual_time||0) > Number(t.estimated_time||0);
              const diff = baselineDiff && baselineDiff[t.task_number];
              const isFocused = idx === focusedRowIdx;
              return (
                <tr
                  key={t.id}
                  data-testid={`task-row-${t.id}`}
                  data-row-idx={idx}
                  className={`hover:bg-slate-900/40 ${isCritical ? "bg-rose-500/5 border-l-2 border-rose-500/60" : ""} ${isFocused ? "ring-2 ring-inset ring-blue-400/80 bg-blue-500/5" : ""}`}
                  onClick={() => setFocusedRowIdx(idx)}
                >
                  <td className="p-2 text-slate-400 mono">
                    <span className="inline-flex items-center gap-1">
                      {t.task_number}
                      {isCritical && <span title="On critical path" className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>}
                    </span>
                  </td>
                  <td className="p-2 min-w-[200px]">
                    <Inline value={t.name||""} onChange={(v)=>updateTask(t.id,{name:v})} dataField="name" />
                    {t.is_sprint && <span className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300" data-testid={`sprint-badge-${t.id}`}>Sprint block</span>}
                    {diff && !diff.isNew && (diff.timeDelta !== 0 || diff.costDelta !== 0) && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`baseline-diff-${t.id}`}>
                        {diff.timeDelta !== 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${diff.timeDelta>0?"border-rose-500/40 bg-rose-500/10 text-rose-300":"border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`}>
                            {diff.timeDelta>0?"+":""}{diff.timeDelta} {timeUnit.toLowerCase()} vs baseline
                          </span>
                        )}
                        {diff.costDelta !== 0 && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${diff.costDelta>0?"border-rose-500/40 bg-rose-500/10 text-rose-300":"border-emerald-500/40 bg-emerald-500/10 text-emerald-300"}`}>
                            {diff.costDelta>0?"+":""}{formatCurrency(diff.costDelta)} vs baseline
                          </span>
                        )}
                      </div>
                    )}
                    {diff && diff.isNew && (
                      <span className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300">New since baseline</span>
                    )}
                  </td>
                  <td className="p-2">
                    <Select value={t.role_id||""} onChange={(v)=>{ const r = roles.find(r=>r.id===v); updateTask(t.id,{role_id:v||null, role_name:r?r.name:null, resource_id:null, resource_name:null}); }}
                      options={[{v:"",l:"—"}, ...roles.map(r=>({v:r.id,l:r.name}))]} />
                  </td>
                  <td className="p-2">
                    <Select value={t.resource_id||""} onChange={(v)=>{ const tm = teammates.find(x=>x.id===v); updateTask(t.id,{resource_id:v||null, resource_name:tm?tm.name:null}); }}
                      options={[{v:"",l:"—"}, ...filteredTeammates(t.role_id).map(tm=>({v:tm.id,l:tm.name}))]} />
                  </td>
                  {showDeps && <>
                    <td className="p-2"><Inline type="number" value={t.order||""} onChange={(v)=>updateTask(t.id,{order:v?Number(v):null})}/></td>
                    <td className="p-2"><Select value={t.order_type||"Start After"} onChange={(v)=>{
                      // Switching away from a lead type → clamp any negative lag back to 0
                      const nextLag = (!LEAD_ORDER_TYPES.has(v) && Number(t.lag||0) < 0) ? 0 : t.lag;
                      updateTask(t.id,{order_type:v, lag: nextLag});
                    }} options={ORDER_TYPES.map(o=>({v:o,l:o}))}/></td>
                    <td className="p-2"><Inline
                      type="number"
                      value={t.lag||0}
                      min={LEAD_ORDER_TYPES.has(t.order_type) ? undefined : 0}
                      onChange={(v)=>{
                        const num = Number(v||0);
                        const isLead = LEAD_ORDER_TYPES.has(t.order_type);
                        if (num < 0 && !isLead) {
                          toast.error('Negative lag only allowed on a lead type ("Start Before"). Change Order Type first.');
                          updateTask(t.id,{lag: 0});
                          return;
                        }
                        updateTask(t.id,{lag:num});
                      }}
                    /></td>
                  </>}
                  {view!=="actuals" && <>
                    <td className="p-2 w-24"><Inline type="number" value={t.estimated_time||0} onChange={(v)=>updateTask(t.id,{estimated_time:Number(v||0)})}/></td>
                    <td className="p-2 w-12 text-center">
                      <input
                        type="checkbox"
                        checked={!!t.extended_hours}
                        onChange={(e)=>updateTask(t.id,{extended_hours: e.target.checked})}
                        data-testid={`task-extended-hours-${t.id}`}
                        title={`${t.extended_hours ? "24h/day" : "8h/day"} for this task — click to toggle`}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-blue-500 focus:ring-blue-500 focus:ring-offset-slate-950 cursor-pointer"
                      />
                    </td>
                    <td className="p-2 text-slate-300 mono">{formatCurrency(estCost)}</td>
                  </>}
                  {view!=="plan" && <>
                    <td className="p-2 w-24"><Inline type="number" value={t.actual_time||""} onChange={(v)=>updateTask(t.id,{actual_time:v?Number(v):null})}/></td>
                    <td className="p-2 w-28">
                      <Inline
                        type="number"
                        value={t.actual_cost ?? ""}
                        placeholder={Number(t.actual_time||0) > 0 ? formatCurrency(autoActualCost) : ""}
                        title={t.actual_cost != null ? "Manually set — clear the field to revert to auto-calc from actual time" : "Auto-calculated from actual time × rate — type a value to override"}
                        onChange={(v)=>updateTask(t.id,{actual_cost: v === "" ? null : Number(v)})}
                      />
                    </td>
                    <td className="p-2"><Inline type="date" value={t.actual_start_date||""} onChange={(v)=>updateTask(t.id,{actual_start_date:v||null})}/></td>
                    <td className="p-2"><Inline type="date" value={t.actual_completion_date||""} onChange={(v)=>updateTask(t.id,{actual_completion_date:v||null})}/></td>
                  </>}
                  {hasSprints && (t.is_sprint ? <><td className="p-2 text-slate-500 text-xs">—</td><td className="p-2 text-slate-500 text-xs">—</td></> : <>
                    <td className="p-2 min-w-[120px]" data-testid={`task-sprint-${t.id}`}>
                      <Select value={t.sprint_number ? String(t.sprint_number) : ""} onChange={(v)=>updateTask(t.id,{sprint_number: v ? Number(v) : null})}
                        options={[{v:"",l:"Backlog"}, ...sprintNumbers.map((n)=>({v:String(n),l:`Sprint ${n}`}))]} />
                    </td>
                    <td className="p-2 w-20" data-testid={`task-points-${t.id}`}>
                      <Inline type="number" min={0} value={t.story_points ?? ""} onChange={(v)=>updateTask(t.id,{story_points: v === "" ? null : Number(v)})} dataField="story_points" />
                    </td>
                  </>)}
                  {extraCols.map((c)=> (
                    <td key={c.k} className={`p-2 ${c.k==="notes" ? "min-w-[220px]" : "min-w-[130px]"}`} data-testid={`task-${c.k}-${t.id}`}>
                      <Inline value={t[c.k]||""} onChange={(v)=>updateTask(t.id,{[c.k]:v})} dataField={c.k} placeholder={c.k==="notes" ? "Add a note" : ""} />
                    </td>
                  ))}
                  {customFields.map((f)=> (
                    <td key={f.key} className="p-2 min-w-[130px]" data-testid={`task-custom-${f.key}-${t.id}`}>
                      <Inline value={(t.custom_fields||{})[f.key]||""} onChange={(v)=>updateCustom(t, f.key, v)} dataField={f.key} />
                    </td>
                  ))}
                  <td className="p-2">
                    <Select value={t.status||"Not Started"} onChange={(v)=>updateTask(t.id,{status:v})} options={STATUS.map(s=>({v:s,l:s}))} />
                  </td>
                  {view==="performance" && (
                    <td className="p-2">
                      {t.actual_cost==null && !flagLate ? <span className="text-slate-500 text-xs">—</span>
                        : taskVar>0 || flagLate
                          ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30"><AlertTriangle size={12}/> At risk</span>
                          : <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"><CheckCircle2 size={12}/> On track</span>}
                    </td>
                  )}
                  <td className="p-2"><button onClick={()=>removeTask(t.id)} data-testid={`delete-task-${t.id}`} className="text-slate-500 hover:text-rose-400"><Trash2 size={14}/></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {view==="performance" && (
        <div className="mt-6 pc-card p-5" data-testid="performance-summary">
          <div className="text-xs uppercase tracking-widest text-blue-300/80 mb-2">Overall health</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <HealthRow label="Schedule" ok={!(scheduleVar>0)} msg={scheduleVar===null?"No baseline due date":scheduleVar>0?`${scheduleVar} days late vs. stated due date`:`${Math.abs(scheduleVar||0)} days ahead`}/>
            <HealthRow label="Budget" ok={budgetVar<=0} msg={budgetVar>0?`${formatCurrency(budgetVar)} over stated budget`:`${formatCurrency(Math.abs(budgetVar))} under budget`}/>
            <HealthRow label="Actual vs Plan — Cost" ok={actualBudgetVar<=0} msg={actualBudgetVar>0?`${formatCurrency(actualBudgetVar)} over plan`:`On plan`}/>
            <HealthRow label="Actual vs Plan — Schedule" ok={!(actualScheduleVar>0)} msg={actualScheduleVar===null?"Not completed":actualScheduleVar>0?`${actualScheduleVar} days late`:`On time`}/>
          </div>
        </div>
      )}

      <Gantt tasks={plan.tasks} timeUnit={timeUnit} />

      <AuditDrawer projectId={id} open={auditOpen} onClose={() => setAuditOpen(false)} />

      <div className="mt-6 text-center">
        <Link to="/app" className="text-sm text-slate-400 hover:text-white">← Back to projects</Link>
      </div>
    </AppLayout>
  );
}

function Field({label, value, onChange, type="text", className="", testid}) {
  return (
    <div className={className}>
      <label className="text-[10px] uppercase tracking-widest text-slate-400">{label}</label>
      <input type={type} value={value||""} onChange={(e)=>onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm" />
    </div>
  );
}

function SelectField({label, value, options, onChange, testid}) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-400">{label}</label>
      <select value={value||""} onChange={(e)=>onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm">
        {options.map((o)=> <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function Inline({value, onChange, type="text", min, dataField, inputRef, placeholder, title}) {
  return <input
    ref={inputRef}
    type={type}
    min={min}
    value={value??""}
    onChange={(e)=>onChange(e.target.value)}
    data-task-field={dataField}
    placeholder={placeholder}
    title={title}
    className="w-full bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 text-sm outline-none placeholder:text-slate-500 placeholder:italic"
  />;
}

function Select({value, onChange, options}) {
  return (
    <select value={value||""} onChange={(e)=>onChange(e.target.value)} className="bg-transparent border border-transparent hover:border-slate-700 focus:border-blue-500 rounded px-2 py-1 text-sm outline-none w-full">
      {options.map((o)=> <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function HealthRow({label, ok, msg}) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${ok?"border-emerald-500/30 bg-emerald-500/5":"border-rose-500/30 bg-rose-500/5"}`}>
      {ok?<CheckCircle2 className="text-emerald-400" size={18}/>:<AlertTriangle className="text-rose-400" size={18}/>}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
        <div className={`text-sm ${ok?"text-emerald-200":"text-rose-200"}`}>{msg}</div>
      </div>
    </div>
  );
}
